import React, { useState, useRef, useCallback, useEffect } from 'react';
import Webcam from 'react-webcam';
import Tesseract from 'tesseract.js';

function preprocessImg(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = 2.5;
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      ctx.filter = 'grayscale(1)';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 1.0));
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

function mapDigit(ch) {
  const C = (ch || '').toUpperCase();
  if (/^[0-9]$/.test(C)) return C;
  const m = { O: '0', Q: '0', D: '0', I: '1', L: '1', Z: '2', S: '5', B: '8', G: '6', T: '7' };
  return m[C] || '';
}

function mapHex(ch) {
  const C = (ch || '').toUpperCase();
  if (/^[0-9A-F]$/.test(C)) return C;
  const m = { O: '0', Q: '0', D: '0', I: '1', L: '1', Z: '2', S: '5', G: '6', T: '7' };
  return m[C] || '';
}

function normalizeSuffix(tail) {
  const compactRaw = (tail || '').replace(/[ -]/g, '').toUpperCase();
  if (!compactRaw) return null;

  const first = mapDigit(compactRaw[0]);
  if (!/^[0-9]$/.test(first)) return null;

  let out = first;
  for (let i = 1; i < compactRaw.length; i++) {
    const h = mapHex(compactRaw[i]);
    if (!/^[0-9A-F]$/.test(h)) return null;
    out += h;
  }

  const groups = out.match(/.{1,4}/g) || [];
  return { compactTail: out, groups };
}

function parseCode(text) {
  if (!text) return null;

  const m = text.match(/\bNR[ ]*([A-Z0-9][A-Z0-9 ]*)/);
  if (!m) return null;

  const captured = m[1] || '';
  const norm = normalizeSuffix(captured);
  if (!norm) return null;

  const compact = 'NR' + norm.compactTail;
  const pretty = 'NR ' + norm.compactTail;
  return { pretty, compact, groups: norm.groups };
}

const TextRead = ({
  disabled = false,
  lang = 'eng',
  onResult,
  autoCloseAfterRead = true,
  attempts: maxAttempts = 4,
}) => {
  const [showCam, setShowCam] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [deviceId, setDeviceId] = useState('');

  const camRef = useRef(null);

  const openCam = useCallback(() => {
    setErrorMsg('');
    setShowCam(true);
  }, []);

  const closeCam = useCallback(() => {
    setShowCam(false);
  }, []);

  const checkDevices = useCallback((devices) => {
    const normalDevices = devices.filter(
      (v) => v.kind === 'videoinput' && v.label.indexOf('camera2 0, facing back') >= 0
    );
    const videoDevices = devices.filter(
      (v) => v.kind === 'videoinput' && v.label.indexOf('facing back') >= 0
    );

    if (normalDevices.length > 0 && videoDevices.length > 0) {
      if (
        normalDevices[0].deviceId !== videoDevices[0].deviceId &&
        normalDevices[0].deviceId !== deviceId
      ) {
        setDeviceId(normalDevices[0].deviceId);
        return false;
      }
    }

    if (normalDevices.length === 0 && videoDevices.length > 0) {
      const oldNormalDevices = devices.filter(
        (v) => v.kind === 'videoinput' && v.label.indexOf('camera 0, facing back') >= 0
      );
      if (oldNormalDevices.length > 0 && oldNormalDevices[0].deviceId !== deviceId) {
        setDeviceId(oldNormalDevices[0].deviceId);
        return false;
      }
    }

    return true;
  }, [deviceId]);

  const enumerateAndSelectBackCamera = useCallback(async () => {
    if (!navigator?.mediaDevices?.enumerateDevices) return;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      checkDevices(devices);
    } catch (error) {
      console.error("Error enumerating devices:", error);
    }
  }, [checkDevices]);

  useEffect(() => {
    if (!navigator?.mediaDevices) return;
    const handler = () => enumerateAndSelectBackCamera();

    try {
      navigator.mediaDevices.addEventListener('devicechange', handler);
    } catch {
      navigator.mediaDevices.ondevicechange = handler;
    }
    return () => {
      try {
        navigator.mediaDevices.removeEventListener('devicechange', handler);
      } catch {
        if (navigator.mediaDevices.ondevicechange === handler) {
          navigator.mediaDevices.ondevicechange = null;
        }
      }
    };
  }, [enumerateAndSelectBackCamera]);

  useEffect(() => {
    if (showCam) {
      enumerateAndSelectBackCamera();
    }
  }, [showCam, enumerateAndSelectBackCamera]);

  const captureAndExtract = useCallback(async () => {
    if (!camRef.current) return;

    setIsReading(true);
    setErrorMsg('');

    try {
      let matched = null;
      let matchDetails = null;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((r) => setTimeout(r, 200));

        const frameDataUrl = camRef.current.getScreenshot();
        if (!frameDataUrl) {
          setErrorMsg('画像を取得できませんでした。もう一度お試しください。');
          continue;
        }

        const preprocessed = await preprocessImg(frameDataUrl);

        const result = await Tesseract.recognize(preprocessed, lang, {
          tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
          tessedit_pageseg_mode: '6'
        });

        const fullText = result?.data?.text || '';
        const parsed = parseCode(fullText);
        if (parsed) {
          matched = parsed;
          matchDetails = {
            psm: '6',
            confidence: result?.data?.confidence,
            fullText,
          };
          break;
        }

      }

      if (!matched) {
        setErrorMsg(
          'nimoca番号が見つかりませんでした。ピントを合わせて明るい場所で読み込んでください。'
        );
      }

      if (onResult) {
        onResult(matched?.compact || '', {
          pretty: matched?.pretty || '',
          groups: matched?.groups || [],
          imageSrc: camRef.current?.getScreenshot() || '',
          ...(matchDetails || {}),
        });
      }

      if (matched && autoCloseAfterRead) {
        setShowCam(false);
      }
    } catch (e) {
      setErrorMsg(e?.message || 'OCRでエラーが発生しました。');
    } finally {
      setIsReading(false);
    }
  }, [lang, onResult, autoCloseAfterRead, maxAttempts]);

  const videoConstraints = deviceId
    ? { deviceId: { exact: deviceId } }
    : { facingMode: 'environment' };

  return (
    <div>
      <div className="d-grid gap-2 mb-3">
        {!showCam &&
          <button
            type="button"
            className="btn btn-active w-100 py-2 mb-1"
            onClick={openCam}
            disabled={disabled || isReading || showCam}
          >
            カード番号読み取り
          </button>
        }
        {showCam &&
          <button
            type="button"
            className="btn btn-active w-100 py-2"
            onClick={closeCam}
            disabled={disabled || isReading || !showCam}
          >
            カメラを閉じる
          </button>
        }
      </div>

      {showCam && (
        <div className="mt-2">
          <div className="mt-2" style={{ width: '100%', height: 150, overflow: 'hidden', borderRadius: 8 }}>
            <Webcam
              audio={false}
              ref={camRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              onUserMedia={enumerateAndSelectBackCamera}
              onUserMediaError={(e) => setErrorMsg(e?.message || 'カメラの起動に失敗しました。')}
              className="cam-video w-full h-full object-cover"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
          <div className='text-left'>
            <span className='text-left mt-2 small text-secondary'>1.カード番号を枠内にすべて収めてください<br />
              2.ピントが合うように静止してください<br />
              3.「番号読み取り」を押すと、<br />　自動で番号が入力されます
            </span>
          </div>

          <div className="d-grid gap-2 mt-2 mb-3">
            <button
              type="button"
              className="btn btn-active w-100 py-2"
              onClick={captureAndExtract}
              disabled={disabled || isReading}
            >
              {isReading ? `読み取り中` : '番号読み取り'}
            </button>
          </div>

          {errorMsg && <div className="text-danger mt-2">{errorMsg}</div>}
        </div>
      )}
    </div>
  );
};

export default TextRead;